import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, CreditCard, LogOut, ChevronDown, LayoutGrid, Building2, Sun, Moon, Palette } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useFeatureFlagsContext } from '@/contexts/FeatureFlagsContext';
import { ACCESS_LEVELS } from '@rti/shared';
import AdminMenuList from './AdminMenuList';

export const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { getFlag, isLoading: isFeatureFlagsLoading } = useFeatureFlagsContext();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const billingEnabled = !isFeatureFlagsLoading && getFlag('enable_billing');
  const currentLevel = user?.effective_membership_id ?? 0;
  // A free trial can preview Compound/Prime-tier features without a real
  // subscription behind it — only hide Billing for someone who's actually
  // paid onto a CRM-managed Compound/Prime plan, not someone trialing one.
  const isCompoundOrPrime = !user?.on_free_trial && (currentLevel === ACCESS_LEVELS.COMPOUND || currentLevel === ACCESS_LEVELS.PRIME);
  const showBilling = billingEnabled && !isCompoundOrPrime;
  const userMenuEnabled = import.meta.env.VITE_ENABLE_USER_MENU === 'true';
  const showCompanySettings = !isFeatureFlagsLoading && getFlag('company_settings');
  const showThemeToggle = !isFeatureFlagsLoading && getFlag('theme_toggle');
  const isEmployee = user?.effective_membership_id === ACCESS_LEVELS.EMPLOYEE;
  const showFullMenu = userMenuEnabled || user?.is_admin || billingEnabled || isEmployee;
  const showSimpleMenu = !showFullMenu;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const parentGroupName = ((user as any).parent_group_name || '').trim();
  const subCompanyName = ((user as any).sub_company_name || '').trim();
  const companyName = (user.company_name || '').trim();
  const effectiveCompanyName = subCompanyName || companyName;
  const companyPrefix = parentGroupName ? `[${parentGroupName}] ${effectiveCompanyName}` : effectiveCompanyName;
  const identityLabel = companyPrefix ? `${companyPrefix} - ${fullName}` : fullName;

  const handleToggleTheme = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    toggleTheme();
    setIsOpen(false);
  };

  if (showSimpleMenu) {
    return (
      <div className="flex items-center gap-2 sm:gap-4">
        <span className="text-rs-off-white text-sm hidden md:inline">
          {identityLabel}
        </span>
        <button
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-lg text-rs-off-white hover:bg-rs-jet transition-colors"
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
        <button
          onClick={logout}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded-lg text-rs-salmon hover:bg-rs-jet transition-colors"
        >
          <LogOut size={16} />
          <span className="text-sm">Logout</span>
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-rs-jet transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-rs-green flex items-center justify-center text-rs-black font-semibold text-sm force-dark-colors">
          {initials}
        </div>
        <span className="hidden lg:inline text-rs-off-white text-sm max-w-[32rem] truncate">
          {identityLabel}
        </span>
        <ChevronDown
          size={16}
          className={`text-rs-off-white transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-rs-jet border border-rs-jet-light rounded-lg shadow-lg py-2 z-50">
          <div className="px-4 py-2 border-b border-rs-jet-light">
            <p className="text-sm font-medium text-rs-off-white">{identityLabel}</p>
            <p className="text-xs text-rs-off-white/60">{user.email}</p>
          </div>

          {showFullMenu && (
            <>
              <Link
                to="/account"
                className="flex items-center gap-3 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <User size={18} className="text-rs-green" />
                <span className="text-sm">Account Settings</span>
              </Link>

              {showCompanySettings && (
                <Link
                  to="/company-settings"
                  className="flex items-center gap-3 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <Building2 size={18} className="text-rs-green" />
                  <span className="text-sm">Company Settings</span>
                </Link>
              )}

              {showBilling && (
                <Link
                  to="/billing"
                  className="flex items-center gap-3 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <CreditCard size={18} className="text-rs-green" />
                  <span className="text-sm">Billing / Subscription</span>
                </Link>
              )}

              {(user?.is_admin || isEmployee) && (
                <>
                  <div className="border-t border-rs-jet-light my-1" />
                  {isEmployee && (
                    <Link
                      to="/system"
                      className="flex items-center gap-3 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors font-semibold"
                      onClick={() => setIsOpen(false)}
                    >
                      <LayoutGrid size={18} className="text-rs-green" />
                      <span className="text-sm">System Hub</span>
                    </Link>
                  )}
                  <AdminMenuList
                    onClose={() => setIsOpen(false)}
                    onAction={() => setIsOpen(false)}
                    iconSize={18}
                    rowClassName="flex items-center gap-3 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors"
                  />
                </>
              )}
            </>
          )}

          <div className="border-t border-rs-jet-light mt-2 pt-2">
            {showThemeToggle && (
              <Link
                to="#"
                onClick={handleToggleTheme}
                className="flex items-center gap-3 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors w-full"
              >
                <Palette size={18} className="text-rs-green" />
                <span className="text-sm">Appearance</span>
              </Link>
            )}
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="flex items-center gap-3 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors w-full border-t border-rs-jet-light mt-1"
            >
              <LogOut size={18} className="text-rs-salmon" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

